import { useState } from "react";

import { DeleteReasonDialog } from "@/components/shared/delete-reason-dialog";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useCancelRiderPayroll,
  useMarkRiderPayrollPaid,
} from "@/hooks/use-rider-payrolls";
import { useRiders } from "@/hooks/use-riders";
import { formatCurrency } from "@/lib/utils";

import { RiderPayrollPdfButton } from "./pdf/rider-payroll-pdf-button";
import { RiderPayrollAdjustmentsTable } from "./rider-payroll-adjustments-table";
import {
  RIDER_PAYROLL_PAYMENT_METHOD_LABELS,
  RIDER_PAYROLL_STATUS_LABELS,
} from "./rider-payroll-labels";
import { RiderPayrollLiveSummary } from "./rider-payroll-live-summary";
import { RiderPayrollMarkPaidDialog } from "./rider-payroll-mark-paid-dialog";
import { RiderPayrollPeriodPicker } from "./rider-payroll-period-picker";
import { RiderPayrollSummaryBox } from "./rider-payroll-summary-box";
import { useRiderPayrollForm } from "./use-rider-payroll-form";

import type { RiderPayroll } from "@artisancode/api-types";

export function RiderPayrollDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: RiderPayroll | null;
}) {
  const canCreate = useHasPermission("rider_payrolls.create");
  const canUpdate = useHasPermission("rider_payrolls.update");
  const canDelete = useHasPermission("rider_payrolls.delete");

  const { data: riderList } = useRiders({ per_page: 100 });
  const riders = riderList?.items ?? [];

  const {
    form,
    fields,
    addAdjustment,
    removeAdjustment,
    onSubmit,
    isSaving,
    isDraft,
  } = useRiderPayrollForm(open, editing, () => onOpenChange(false));

  const watchedRiderId = form.watch("riderId");

  const { mutate: markPaid, isPending: isMarkingPaid } =
    useMarkRiderPayrollPaid();
  const { mutate: cancelPayroll, isPending: isCancelling } =
    useCancelRiderPayroll();

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [cancelOpen, setCancelOpen] = useState(false);

  const isMobile = useIsMobile();
  const title = editing ? "Detail Penggajian Rider" : "Buat Penggajian Rider";
  const readOnly = !isDraft;

  function handleMarkPaid() {
    if (!editing) return;
    markPaid(
      { id: editing.id, paymentDate },
      {
        onSuccess: () => {
          setPayDialogOpen(false);
          onOpenChange(false);
        },
      },
    );
  }

  function handleCancel(reason: string) {
    if (!editing) return;
    cancelPayroll(
      { id: editing.id, reason },
      {
        onSuccess: () => {
          setCancelOpen(false);
          onOpenChange(false);
        },
      },
    );
  }

  const footer = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        {readOnly ? "Tutup" : "Batal"}
      </Button>
      {editing && editing.status === "paid" && (
        <RiderPayrollPdfButton payroll={editing} />
      )}
      {editing && editing.status !== "cancelled" && canDelete && (
        <Button
          type="button"
          variant="outline"
          className="text-destructive hover:bg-destructive/10"
          onClick={() => setCancelOpen(true)}
        >
          Batalkan
        </Button>
      )}
      {!readOnly && (
        <Button
          type="submit"
          form="rider-payroll-form"
          disabled={
            isSaving || (!editing && !canCreate) || (!!editing && !canUpdate)
          }
        >
          {isSaving ? "Menyimpan..." : "Simpan Draft"}
        </Button>
      )}
      {isDraft && editing && canUpdate && (
        <Button type="button" onClick={() => setPayDialogOpen(true)}>
          Tandai Dibayar
        </Button>
      )}
    </>
  );

  const formContent = (
    <Form {...form}>
      <form
        id="rider-payroll-form"
        onSubmit={onSubmit}
        className="space-y-4 py-2"
      >
        {editing && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={
                editing.status === "paid"
                  ? "default"
                  : editing.status === "cancelled"
                    ? "destructive"
                    : "outline"
              }
            >
              {RIDER_PAYROLL_STATUS_LABELS[editing.status]}
            </Badge>
            {editing.transactionCode && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
              >
                {editing.transactionCode}
              </Badge>
            )}
            {editing.paymentDate && (
              <span className="text-xs text-muted-foreground">
                Dibayar {editing.paymentDate}
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="riderId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rider</FormLabel>
                <FormControl>
                  <SearchableSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={riders.map((r) => ({
                      value: r.id,
                      label: r.name,
                    }))}
                    placeholder="Cari rider..."
                    disabled={!!editing}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="paymentMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Metode Bayar</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={readOnly}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(RIDER_PAYROLL_PAYMENT_METHOD_LABELS).map(
                      ([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {editing && readOnly ? (
          <p className="text-sm">
            Periode:{" "}
            <span className="font-medium">
              {editing.periodStart} s/d {editing.periodEnd}
            </span>
          </p>
        ) : (
          <RiderPayrollPeriodPicker
            riderId={watchedRiderId}
            payrollId={editing?.id ?? null}
            selectedIds={form.watch("riderSaleIds")}
            onChangeSelected={(ids) => form.setValue("riderSaleIds", ids)}
            readOnly={readOnly}
          />
        )}

        {readOnly && editing && <RiderPayrollSummaryBox editing={editing} />}

        <RiderPayrollAdjustmentsTable
          form={form}
          fields={fields}
          onAddAdjustment={addAdjustment}
          onRemoveAdjustment={removeAdjustment}
          readOnly={readOnly}
        />

        {!readOnly && (
          <RiderPayrollLiveSummary
            riderId={watchedRiderId}
            riderSaleIds={form.watch("riderSaleIds")}
            adjustments={form.watch("adjustments")}
          />
        )}

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Catatan</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Catatan (opsional)"
                  className="min-h-16 resize-none"
                  disabled={readOnly}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {readOnly && editing && (
          <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
            <span className="text-sm font-semibold">Gaji Bersih</span>
            <span className="text-base font-bold text-foreground">
              {formatCurrency(editing.netAmount)}
            </span>
          </div>
        )}
      </form>
    </Form>
  );

  return (
    <>
      {isMobile ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="bottom"
            className="flex h-[92dvh] flex-col gap-0 overflow-hidden rounded-t-2xl p-4"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <SheetHeader className="p-0 pb-3 text-left">
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">{formContent}</div>
            <SheetFooter className="flex-row flex-wrap justify-end gap-2 border-t p-0 pt-3">
              {footer}
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-h-[92dvh] overflow-y-auto p-4 sm:p-6 sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
            {formContent}
            <DialogFooter className="flex flex-row flex-wrap justify-end gap-2 border-t pt-3 mt-4">
              {footer}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <RiderPayrollMarkPaidDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        paymentDate={paymentDate}
        onPaymentDateChange={setPaymentDate}
        onConfirm={handleMarkPaid}
        isPending={isMarkingPaid}
      />

      <DeleteReasonDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Batalkan Penggajian Rider"
        description={
          editing
            ? `Payroll "${editing.riderName}" (${editing.periodStart} - ${editing.periodEnd}) akan dibatalkan.`
            : undefined
        }
        onConfirm={handleCancel}
        loading={isCancelling}
      />
    </>
  );
}
